import type { CheckHotelAvailabilityQueryVariables } from '#build/graphql-operations'
import { useLocalStorage } from '@vueuse/core'

type HotelAvailabilityState = {
  dateFrom: string // YYYY-MM-DD
  dateTo: string // YYYY-MM-DD
  rooms: number
  people: number
}

type UseHotelAvailabilityState = {
  getStoredDateFrom(): string
  getStoredDateTo(): string
  getStoredPeople(): number
  getStoredRooms(): number
  getQueryVariables(nid: string): CheckHotelAvailabilityQueryVariables | null
  persistState(state: HotelAvailabilityState): void
}

/**
 * Helper function to parse "YYYY-MM-DD" as a local Date at 00:00 local time.
 */
function parseLocalDate(dateString: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return null
  }

  const [yearStr, monthStr, dayStr] = dateString.split('-')
  const year = Number(yearStr)

  // Months are 0-based in JS.
  const month = Number(monthStr) - 1
  const day = Number(dayStr)

  // Local time.
  const dateObj = new Date(year, month, day)

  // Check for an invalid date.
  if (Number.isNaN(dateObj.getTime())) {
    return null
  }

  return dateObj
}

/**
 * Returns a Date representing "today" (local) at 00:00.
 */
function getStartOfToday(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export function useHotelAvailabilityState(): UseHotelAvailabilityState {
  const state = useLocalStorage<HotelAvailabilityState>(
    'hotelAvailabilityState',
    () => ({
      dateFrom: '',
      dateTo: '',
      people: 1,
      rooms: 1,
    }),
  )

  /**
   * Only return the stored `dateFrom` if:
   * - format is valid (YYYY-MM-DD)
   * - parsed dateFrom >= today (in local time)
   */
  function getStoredDateFrom(): string {
    const parsed = parseLocalDate(state.value.dateFrom)
    if (!parsed) {
      return ''
    }

    const today = getStartOfToday()
    if (parsed < today) {
      return ''
    }

    // If valid and >= today
    return state.value.dateFrom
  }

  /**
   * Only return the stored `dateTo` if:
   * - format is valid (YYYY-MM-DD)
   * - dateTo > dateFrom (in local time)
   *   (Also implicitly checks that dateFrom is valid + >= today)
   */
  function getStoredDateTo(): string {
    const validDateFromStr = getStoredDateFrom()
    if (!validDateFromStr) {
      return ''
    }
    const parsedDateFrom = parseLocalDate(validDateFromStr)!

    const parsedDateTo = parseLocalDate(state.value.dateTo)
    if (!parsedDateTo) {
      return ''
    }

    if (parsedDateTo <= parsedDateFrom) {
      return ''
    }

    return state.value.dateTo
  }

  function getStoredRooms(): number {
    return state.value.rooms
  }

  function getStoredPeople(): number {
    return state.value.people
  }

  function persistState(newState: HotelAvailabilityState) {
    state.value.dateFrom = newState.dateFrom
    state.value.dateTo = newState.dateTo
    state.value.people = newState.people
    state.value.rooms = newState.rooms
  }

  /**
   * Returns CheckHotelAvailabilityQueryVariables if dateFrom & dateTo are valid,
   * otherwise returns null.
   */
  function getQueryVariables(
    nid: string,
  ): CheckHotelAvailabilityQueryVariables | null {
    const dateFrom = getStoredDateFrom()
    const dateTo = getStoredDateTo()
    const rooms = getStoredRooms()
    const people = getStoredPeople()

    // If either date is invalid/empty or rooms/people are invalid, return null.
    if (!dateFrom || !dateTo || !rooms || !people) {
      return null
    }

    return {
      nid,
      dateFrom,
      dateTo,
      rooms,
      people,
    }
  }

  return {
    getStoredDateFrom,
    getStoredDateTo,
    getStoredPeople,
    getStoredRooms,
    persistState,
    getQueryVariables,
  }
}
